import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { listRoles, type Role } from '../../api/rolesApi.js';
import { listUsers, type User } from '../../api/usersApi.js';

type Mode = 'CHOOSE' | 'NEW' | 'MODIFY';

export function UserAccessEntryPage(): ReactNode {
  const { token } = useAuth();
  const nav = useNavigate();

  const [mode, setMode] = useState<Mode>('CHOOSE');
  const [roles, setRoles] = useState<readonly Role[]>([]);
  const [users, setUsers] = useState<readonly User[]>([]);
  const [roleId, setRoleId] = useState('');
  const [userId, setUserId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    let alive = true;
    void listRoles(token)
      .then((r) => { if (alive) setRoles(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  // Asking the server for one role's holders keeps the page correct past the
  // first 200 users, which a client-side filter over one page could not be.
  useEffect(() => {
    if (mode !== 'MODIFY' || !roleId) { setUsers([]); return; }
    let alive = true;
    setLoadingUsers(true);
    void listUsers({ defaultRoleId: roleId, limit: 200 }, token)
      .then((r) => { if (alive) setUsers(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoadingUsers(false); });
    return () => { alive = false; };
  }, [mode, roleId, token]);

  const usersForRole = users;

  const selectedRole = useMemo(
    () => roles.find((r) => r.roleId === roleId) ?? null,
    [roles, roleId],
  );

  /** A vendor firm and a field force worker are different records with their
   *  own statutory fields, so each role category has its own creation screen. */
  const createPathFor = (categoryCode: string, id: string): string => {
    if (categoryCode === 'VENDOR') return '/admin/vendors/new';
    if (categoryCode === 'WORKFORCE') return '/admin/field-force/new';
    return `/admin/users/new/form?roleId=${encodeURIComponent(id)}`;
  };

  const reset = (next: Mode) => {
    setMode(next); setRoleId(''); setUserId(''); setError(null);
  };

  return (
    <main aria-label="user-access-entry">
      <h1>User Management</h1>

      <div role="group" aria-label="user-action">
        <button type="button" aria-pressed={mode === 'NEW'} onClick={() => reset('NEW')}>
          New user
        </button>
        <button type="button" aria-pressed={mode === 'MODIFY'} onClick={() => reset('MODIFY')}>
          Modify existing user
        </button>
      </div>

      {error ? <p role="alert">{error}</p> : null}

      {mode === 'CHOOSE' ? (
        <p>Choose whether to create a new user or modify an existing one.</p>
      ) : null}

      {mode !== 'CHOOSE' ? (
        <section aria-label="role-selection">
          <label htmlFor="entry-role">Role</label>
          <select
            id="entry-role"
            value={roleId}
            onChange={(event) => { setRoleId(event.target.value); setUserId(''); }}
          >
            <option value="">Select a role</option>
            {roles.map((role) => (
              <option key={role.roleId} value={role.roleId}>{role.roleName}</option>
            ))}
          </select>
        </section>
      ) : null}

      {mode === 'NEW' && roleId ? (
        <section aria-label="create-continue">
          <p>
            {selectedRole?.roleCategoryCode === 'VENDOR'
              ? 'The vendor form captures the firm’s GST, PAN, TAN, address and bank details along with its login.'
              : selectedRole?.roleCategoryCode === 'WORKFORCE'
                ? 'The field force form captures the worker, the vendor they work under, and their login.'
                : 'The creation form for this role captures its details and the login together.'}
          </p>
          <button
            type="button"
            onClick={() => nav(createPathFor(selectedRole?.roleCategoryCode ?? '', roleId))}
          >
            Continue
          </button>
        </section>
      ) : null}

      {mode === 'MODIFY' && roleId ? (
        <section aria-label="modify-user-selection">
          {loadingUsers ? <p>Loading users…</p> : null}
          {!loadingUsers && usersForRole.length === 0 ? (
            <p>No users exist for this role yet.</p>
          ) : null}
          {usersForRole.length > 0 ? (
            <>
              <label htmlFor="entry-user">User</label>
              <select
                id="entry-user"
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
              >
                <option value="">Select a user</option>
                {usersForRole.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {user.employeeCode ? `${user.employeeCode} — ` : ''}{user.displayName}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!userId}
                onClick={() => nav(`/admin/users/${userId}/edit`)}
              >
                Open for editing
              </button>
            </>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}
